/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Count } from '../models/count';
import { NewParticipant } from '../models/new-participant';
import { Participant } from '../models/participant';
import { ParticipantPartial } from '../models/participant-partial';
import { ParticipantWithRelations } from '../models/participant-with-relations';
import { Tour } from '../models/tour';

@Injectable({
  providedIn: 'root',
})
export class ParticipantControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation participantControllerCreateAll
   */
  static readonly ParticipantControllerCreateAllPath = '/participants/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll$Response(params?: {
    body?: Array<NewParticipant>
  }): Observable<StrictHttpResponse<Array<Participant>>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Participant>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll(params?: {
    body?: Array<NewParticipant>
  }): Observable<Array<Participant>> {

    return this.createAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Participant>>) => r.body as Array<Participant>)
    );
  }

  /**
   * Path part for operation participantControllerCount
   */
  static readonly ParticipantControllerCountPath = '/participants/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<Count> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation participantControllerFindTour
   */
  static readonly ParticipantControllerFindTourPath = '/participants/{id}/tour';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findTour()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTour$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<Tour>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerFindTourPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Tour>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findTour$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTour(params: {
    id: string;
  }): Observable<Tour> {

    return this.findTour$Response(params).pipe(
      map((r: StrictHttpResponse<Tour>) => r.body as Tour)
    );
  }

  /**
   * Path part for operation participantControllerFindById
   */
  static readonly ParticipantControllerFindByIdPath = '/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ParticipantWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ParticipantWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<ParticipantWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<ParticipantWithRelations>) => r.body as ParticipantWithRelations)
    );
  }

  /**
   * Path part for operation participantControllerReplaceById
   */
  static readonly ParticipantControllerReplaceByIdPath = '/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById$Response(params: {
    id: string;
    body?: Participant
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById(params: {
    id: string;
    body?: Participant
  }): Observable<void> {

    return this.replaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation participantControllerDeleteById
   */
  static readonly ParticipantControllerDeleteByIdPath = '/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation participantControllerUpdateById
   */
  static readonly ParticipantControllerUpdateByIdPath = '/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: ParticipantPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: ParticipantPartial
  }): Observable<void> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation participantControllerFind
   */
  static readonly ParticipantControllerFindPath = '/participants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ParticipantWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ParticipantWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<ParticipantWithRelations>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ParticipantWithRelations>>) => r.body as Array<ParticipantWithRelations>)
    );
  }

  /**
   * Path part for operation participantControllerCreate
   */
  static readonly ParticipantControllerCreatePath = '/participants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewParticipant
  }): Observable<StrictHttpResponse<Participant>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Participant>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewParticipant
  }): Observable<Participant> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<Participant>) => r.body as Participant)
    );
  }

  /**
   * Path part for operation participantControllerUpdateAll
   */
  static readonly ParticipantControllerUpdateAllPath = '/participants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAll$Response(params?: {
    where?: any;
    body?: ParticipantPartial
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerUpdateAllPath, 'patch');
    if (params) {
      rb.query('where', params.where, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAll(params?: {
    where?: any;
    body?: ParticipantPartial
  }): Observable<Count> {

    return this.updateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation participantControllerProxyCreateAll
   */
  static readonly ParticipantControllerProxyCreateAllPath = '/proxy/participants/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll$Response(params?: {
    body?: Array<NewParticipant>
  }): Observable<StrictHttpResponse<Array<Participant>>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Participant>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll(params?: {
    body?: Array<NewParticipant>
  }): Observable<Array<Participant>> {

    return this.proxyCreateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Participant>>) => r.body as Array<Participant>)
    );
  }

  /**
   * Path part for operation participantControllerProxyCount
   */
  static readonly ParticipantControllerProxyCountPath = '/proxy/participants/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount(params?: {
    where?: any;
  }): Observable<Count> {

    return this.proxyCount$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation participantControllerProxyFindTour
   */
  static readonly ParticipantControllerProxyFindTourPath = '/proxy/participants/{id}/tour';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindTour()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindTour$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<Tour>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyFindTourPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Tour>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindTour$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindTour(params: {
    id: string;
  }): Observable<Tour> {

    return this.proxyFindTour$Response(params).pipe(
      map((r: StrictHttpResponse<Tour>) => r.body as Tour)
    );
  }

  /**
   * Path part for operation participantControllerProxyFindById
   */
  static readonly ParticipantControllerProxyFindByIdPath = '/proxy/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ParticipantWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ParticipantWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById(params: {
    id: string;
    filter?: any;
  }): Observable<ParticipantWithRelations> {

    return this.proxyFindById$Response(params).pipe(
      map((r: StrictHttpResponse<ParticipantWithRelations>) => r.body as ParticipantWithRelations)
    );
  }

  /**
   * Path part for operation participantControllerProxyReplaceById
   */
  static readonly ParticipantControllerProxyReplaceByIdPath = '/proxy/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyReplaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById$Response(params: {
    id: string;
    body?: Participant
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyReplaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById(params: {
    id: string;
    body?: Participant
  }): Observable<void> {

    return this.proxyReplaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation participantControllerProxyDeleteById
   */
  static readonly ParticipantControllerProxyDeleteByIdPath = '/proxy/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyDeleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyDeleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById(params: {
    id: string;
  }): Observable<void> {

    return this.proxyDeleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation participantControllerProxyUpdateById
   */
  static readonly ParticipantControllerProxyUpdateByIdPath = '/proxy/participants/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById$Response(params: {
    id: string;
    body?: ParticipantPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById(params: {
    id: string;
    body?: ParticipantPartial
  }): Observable<void> {

    return this.proxyUpdateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation participantControllerProxyFind
   */
  static readonly ParticipantControllerProxyFindPath = '/proxy/participants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFind()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ParticipantWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ParticipantWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFind$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind(params?: {
    filter?: any;
  }): Observable<Array<ParticipantWithRelations>> {

    return this.proxyFind$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ParticipantWithRelations>>) => r.body as Array<ParticipantWithRelations>)
    );
  }

  /**
   * Path part for operation participantControllerProxyCreate
   */
  static readonly ParticipantControllerProxyCreatePath = '/proxy/participants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate$Response(params?: {
    body?: NewParticipant
  }): Observable<StrictHttpResponse<Participant>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Participant>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate(params?: {
    body?: NewParticipant
  }): Observable<Participant> {

    return this.proxyCreate$Response(params).pipe(
      map((r: StrictHttpResponse<Participant>) => r.body as Participant)
    );
  }

  /**
   * Path part for operation participantControllerProxyUpdateAll
   */
  static readonly ParticipantControllerProxyUpdateAllPath = '/proxy/participants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateAll$Response(params?: {
    where?: any;
    body?: ParticipantPartial
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ParticipantControllerService.ParticipantControllerProxyUpdateAllPath, 'patch');
    if (params) {
      rb.query('where', params.where, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateAll(params?: {
    where?: any;
    body?: ParticipantPartial
  }): Observable<Count> {

    return this.proxyUpdateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

}
